/**
 * StatusBadge — Shared badge component for consistent status chip styling.
 *
 * Usage:
 *   <StatusBadge variant="success">Eligible</StatusBadge>
 *   <StatusBadge variant="danger">Ineligible</StatusBadge>
 *   <StatusBadge status="Approved" map={LOAN_STATUS_MAP} />
 *
 * Renders a MUI Chip with a soft (light bg + colored text) appearance.
 * Pass `solid` to use the filled style instead.
 */
import { Chip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Roles } from '../../constants';

// Badge colors live in the theme (palette.badges) so they adapt to light/dark.
const BADGE_VARIANTS = ['success', 'danger', 'warning', 'info', 'primary', 'secondary', 'purple', 'cyan', 'teal', 'azure'];


// ── Pre-built status maps for common domains ──

export const ELIGIBILITY_MAP = {
  Eligible:   { variant: 'success', label: 'Eligible' },
  Ineligible: { variant: 'danger',  label: 'Ineligible' },
};

export const USER_STATUS_MAP = {
  Active:                 { variant: 'success',  label: 'Active' },
  Inactive:               { variant: 'danger',   label: 'Inactive' },
  PendingApproval:        { variant: 'purple',   label: 'Pending Approval' },
  'Pending Approval':     { variant: 'purple',   label: 'Pending Approval' },
  PendingConfirmation:    { variant: 'warning',  label: 'Pending Confirmation' },
  'Pending Confirmation': { variant: 'warning',  label: 'Pending Confirmation' },
};

export const BROKER_STATUS_MAP = {
  Active:             { variant: 'success', label: 'Active' },
  'Pre-Approved':     { variant: 'cyan',    label: 'Pre-Approved' },
  'Pending Approval': { variant: 'purple',  label: 'Pending Approval' },
  Inactive:           { variant: 'danger',  label: 'Inactive' },
};

export const LOAN_STATUS_MAP = {
  Origination:             { variant: 'cyan',      label: 'Origination' },
  Processing:              { variant: 'cyan',      label: 'Processing' },
  Underwriting:            { variant: 'info',      label: 'Underwriting' },
  'In Underwriting':       { variant: 'info',      label: 'In Underwriting' },
  Conditions:              { variant: 'warning',   label: 'Conditions' },
  'Conditional Approval':  { variant: 'warning',   label: 'Conditional Approval' },
  Approved:                { variant: 'success',   label: 'Approved' },
  'Clear to Close':        { variant: 'teal',      label: 'Clear to Close' },
  CTC:                     { variant: 'teal',      label: 'CTC' },
  Closed:                  { variant: 'success',   label: 'Closed' },
  Funded:                  { variant: 'success',   label: 'Funded' },
  Suspended:               { variant: 'danger',    label: 'Suspended' },
  Denied:                  { variant: 'danger',    label: 'Denied' },
  Withdrawn:               { variant: 'danger',    label: 'Withdrawn' },
};

export const CONDITION_STATUS_MAP = {
  Ready:     { variant: 'info',      label: 'Ready' },
  NotReady:  { variant: 'warning',   label: 'Not Ready' },
  Completed: { variant: 'success',   label: 'Completed' },
  Cancelled: { variant: 'secondary', label: 'Cancelled' },
  Waived:    { variant: 'purple',    label: 'Waived' },
};

export const CONDITION_APPROVAL_MAP = {
  NotReadyToApprove:       { variant: 'warning',   label: 'Not Ready to Approve' },
  ReadyToApprove:          { variant: 'info',       label: 'Ready to Approve' },
  Approved:                { variant: 'success',    label: 'Approved' },
  AutomaticallyCancelled:  { variant: 'secondary',  label: 'Auto-Cancelled' },
  ManuallyCancelled:       { variant: 'secondary',  label: 'Cancelled' },
};

export const SEVERITY_MAP = {
  Minor:    { variant: 'info',    label: 'Minor' },
  Moderate: { variant: 'warning', label: 'Moderate' },
  High:     { variant: 'danger',  label: 'High' },
};

export const DOCUMENT_STATUS_MAP = {
  Uploaded: { variant: 'warning', label: 'Uploaded' },
  Reviewed: { variant: 'info',    label: 'Reviewed' },
  Accepted: { variant: 'success', label: 'Accepted' },
  Archived: { variant: 'secondary', label: 'Archived' },
  Expired:  { variant: 'danger',  label: 'Expired' },
};

export const ROLE_MAP = {
  LenderAdmin:      { variant: 'purple', label: 'Lender Admin' },
  LoanOfficer:      { variant: 'info',   label: 'Loan Officer' },
  AccountExecutive: { variant: 'azure',  label: 'Account Executive' },
  BrokerAdmin:      { variant: 'purple', label: 'Broker Admin' },
  Broker:           { variant: 'info',   label: 'Broker' },
  Processor:        { variant: 'teal',   label: 'Processor' },
  [Roles.BrokerFullAccessReadOnly]: { variant: 'secondary', label: 'Read-only' },
};

export const AUS_RECOMMENDATION_MAP = {
  approve: { variant: 'success', label: '✓ Approved' },
  accept:  { variant: 'success', label: '✓ Approved' },
  refer:   { variant: 'warning', label: '⚠ Refer' },
  caution: { variant: 'warning', label: '⚠ Refer' },
};

export const REP_WARRANTY_MAP = {
  Granted: { variant: 'success', label: 'Granted' },
  Partial: { variant: 'warning', label: 'Partial' },
  Denied:  { variant: 'danger',  label: 'Denied' },
};

export const PIPELINE_STATUS_MAP = {
  Submitted:   { variant: 'info',      label: 'Submitted' },
  'In Review': { variant: 'warning',   label: 'In Review' },
  Approved:    { variant: 'success',   label: 'Approved' },
  Locked:      { variant: 'purple',    label: 'Locked' },
  Funded:      { variant: 'cyan',      label: 'Funded' },
};

export const COC_STATUS_MAP = {
  Submitted:            { variant: 'info',      label: 'Submitted' },
  'Under Review':       { variant: 'warning',   label: 'Under Review' },
  Approved:             { variant: 'success',   label: 'Approved' },
  Rejected:             { variant: 'danger',    label: 'Rejected' },
  'More Info Required': { variant: 'warning',   label: 'More Info Required' },
};

/**
 * @param {object} props
 * @param {'success'|'danger'|'warning'|'info'|'primary'|'secondary'|'purple'|'cyan'|'teal'|'azure'} [props.variant]
 * @param {string} [props.status] - Status key to look up in the provided map
 * @param {object} [props.map] - Status map (key → { variant, label })
 * @param {boolean} [props.solid] - Use filled (solid) style instead of light
 * @param {object} [props.sx] - Additional MUI sx styles
 * @param {React.ReactNode} [props.children] - Badge content (overrides map label)
 */
const StatusBadge = ({ variant, status, map, solid, sx, children, ...rest }) => {
  const theme = useTheme();
  let resolvedVariant = variant || 'secondary';
  let resolvedLabel = children;

  if (status != null && map) {
    const entry = map[status];
    if (entry) {
      resolvedVariant = entry.variant || resolvedVariant;
      resolvedLabel = resolvedLabel || entry.label;
    } else {
      resolvedLabel = resolvedLabel || status;
    }
  }

  if (!BADGE_VARIANTS.includes(resolvedVariant)) resolvedVariant = 'secondary';
  const badges = theme.vars?.palette?.badges || theme.palette.badges;
  const colors = badges[resolvedVariant];

  return (
    <Chip
      label={resolvedLabel}
      size="small"
      sx={{
        fontWeight: 600,
        fontSize: '0.75rem',
        bgcolor: solid ? colors.color : colors.bg,
        color: solid ? 'background.paper' : colors.color,
        ...sx,
      }}
      {...rest}
    />
  );
};

export default StatusBadge;
